import type { Comic } from '@panels/shared';
import { useState } from 'react';
import { ApiError, rotateShareToken, updateSheet } from '../lib/api.js';
import { copyComicToClipboard, copyText, downloadComicPng } from '../lib/export.js';

interface Props {
  comic: Comic;
  /** Absent on public share pages, where there is nobody to authorize. */
  editable?: boolean;
  onChange?: (comic: Comic) => void;
}

/** Share, download, copy, hide — all rendered from the same op log (§9). */
export function ComicActions({ comic, editable = false, onChange }: Props) {
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shareToken, setShareToken] = useState(comic.shareToken);

  const flash = (message: string) => {
    setNote(message);
    setTimeout(() => setNote(null), 2400);
  };

  const shareUrl = `${window.location.origin}/c/${shareToken}`;

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: comic.title ?? 'A Panels comic', url: shareUrl });
        return;
      } catch {
        // Sheet dismissed — fall through to copying the link.
      }
    }
    flash((await copyText(shareUrl)) ? 'Link copied' : 'Could not copy the link');
  };

  const download = async () => {
    setBusy(true);
    try {
      await downloadComicPng(comic);
      flash('Saved');
    } catch {
      flash('Could not save that image');
    } finally {
      setBusy(false);
    }
  };

  const copyImage = async () => {
    setBusy(true);
    const result = await copyComicToClipboard(comic);
    setBusy(false);
    flash(
      result === 'copied'
        ? 'Comic copied'
        : result === 'unsupported'
          ? 'This browser cannot copy images — try Save instead'
          : 'Could not copy that image',
    );
  };

  const toggleHidden = async () => {
    setBusy(true);
    try {
      // Guardrail #10: hiding, never deleting.
      const updated = await updateSheet(comic.sheetId, { hidden: !comic.hidden });
      onChange?.(updated);
      flash(updated.hidden ? 'Hidden from the gallery' : 'Back in the gallery');
    } catch (err) {
      flash(err instanceof ApiError ? err.message : 'Could not update that');
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    setBusy(true);
    try {
      const result = await rotateShareToken(comic.sheetId);
      setShareToken(result.shareToken);
      flash('Old links no longer work');
    } catch (err) {
      flash(err instanceof ApiError ? err.message : 'Could not change the link');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="comic-actions">
      <div className="action-row">
        <button type="button" className="button" onClick={share} disabled={busy}>
          Share link
        </button>
        <button type="button" className="button" onClick={download} disabled={busy}>
          Save PNG
        </button>
        <button type="button" className="button" onClick={copyImage} disabled={busy}>
          Copy image
        </button>
      </div>

      {editable ? (
        <div className="action-row secondary">
          <button type="button" className="link-button" onClick={toggleHidden} disabled={busy}>
            {comic.hidden ? 'Unhide' : 'Hide from gallery'}
          </button>
          <button type="button" className="link-button" onClick={rotate} disabled={busy}>
            New link (revokes the old one)
          </button>
        </div>
      ) : null}

      {note ? <p className="action-note">{note}</p> : null}
    </div>
  );
}
